/**
 * Equipment slot vocabulary at the server-schema/client-inventory boundary.
 *
 * The server schema uses body-oriented names (`mainHand`, `head`, `chest`),
 * while generated item content and the equipment window use item slots
 * (`weapon`, `hat`, `top`). Keep the mapping here so inventory snapshots,
 * validation, and both runtimes cannot silently drift apart.
 */
export const EQUIPMENT_SCHEMA_SLOTS = [
  'mainHand',
  'head',
  'chest',
  'legs',
  'feet',
  'hands',
  'back',
  'neck',
  // Lane BK (B26): the Lantern slot. Appended last — an additive schema field,
  // so old saves load it empty and old clients ignore it.
  'charm',
  // The Flame slot (DJ 2026-10-23): a small flame floating above the head.
  // Appended last for the same reason as the Lantern.
  'halo',
] as const;

export type EquipmentSchemaSlot = (typeof EQUIPMENT_SCHEMA_SLOTS)[number];

export const ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT = {
  weapon: 'mainHand',
  hat: 'head',
  top: 'chest',
  bottom: 'legs',
  shoes: 'feet',
  gloves: 'hands',
  cape: 'back',
  accessory: 'neck',
  lantern: 'charm',
  flame: 'halo',
} as const satisfies Record<string, EquipmentSchemaSlot>;

export type ItemEquipmentSlot = keyof typeof ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT;

export const EQUIPMENT_SCHEMA_SLOT_TO_ITEM_SLOT: Record<EquipmentSchemaSlot, ItemEquipmentSlot> = {
  mainHand: 'weapon',
  head: 'hat',
  chest: 'top',
  legs: 'bottom',
  feet: 'shoes',
  hands: 'gloves',
  back: 'cape',
  neck: 'accessory',
  charm: 'lantern',
  halo: 'flame',
};

/**
 * Player-facing slot names, keyed by the internal item slot. The keys are
 * storage (saves, wire, content) and never change; the labels are
 * presentation. `shoes` reads **Trail** since lane BD (D46): player ghosts
 * have no feet, and the slot's item styles the wisp's light-mote trail.
 * `lantern` (schema `charm`, lane BK / B26) holds the small light the ghost
 * carries — src/shared/lanternStyle.ts. `flame` (schema `halo`) holds the
 * flame that floats above the head — the same light in crown placement.
 */
export const ITEM_SLOT_LABELS: Readonly<Record<ItemEquipmentSlot, string>> = {
  weapon: 'Weapon',
  hat: 'Head',
  top: 'Chest',
  bottom: 'Legs',
  shoes: 'Trail',
  gloves: 'Hands',
  cape: 'Back',
  accessory: 'Neck',
  lantern: 'Lantern',
  flame: 'Flame',
};

/** The player-facing name of a schema (body) slot — `feet` reads "Trail". */
export function schemaSlotLabel(slot: EquipmentSchemaSlot): string {
  return ITEM_SLOT_LABELS[EQUIPMENT_SCHEMA_SLOT_TO_ITEM_SLOT[slot]];
}

/**
 * The kind word an item listing shows next to its rarity: Trail items read
 * "trail", Lantern items "lantern" and Flame items "flame" instead of the generic "armor";
 * everything else keeps its type.
 */
export function itemKindLabel(type: string | undefined, equipmentSlot: string | undefined): string | undefined {
  if (equipmentSlot === 'shoes') return 'trail';
  if (equipmentSlot === 'lantern') return 'lantern';
  if (equipmentSlot === 'flame') return 'flame';
  return type;
}
